import type { Dayjs } from 'dayjs';
import type { CricketFixture, UpdateCricketFixtureBody } from '@/types/matchPushes';
import { fromIstWallClock } from '@/utils/cricketCalendar';

/** What the edit form holds (times as an IST wall-clock picker value). */
export interface FixtureFormInput {
  team1Name: string;
  team2Name: string;
  startsAt: Dayjs;
  seriesName?: string | null;
  matchLabel?: string | null;
  venue?: string | null;
  audienceCountries: string[];
  tracked: boolean;
  /** Only present when the match can be set upcoming/cancelled. */
  status?: 'upcoming' | 'cancelled';
}

const MINUTE_MS = 60 * 1000;
export const minuteOf = (iso: string) => Math.floor(new Date(iso).getTime() / MINUTE_MS);

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);

/**
 * The PATCH body for an edit: only the fields whose value differs from the
 * match. The backend locks every field it is sent a change for, so sending
 * unchanged fields would freeze them against the daily sync. The start time is
 * compared at minute precision (the picker has no seconds). An optional text
 * field the admin cleared is sent as null so it can be blanked.
 */
export function fixtureEditDiff(
  fixture: CricketFixture,
  input: FixtureFormInput
): UpdateCricketFixtureBody {
  const body: UpdateCricketFixtureBody = {};

  const team1Name = input.team1Name.trim();
  if (team1Name !== fixture.team1Name) body.team1Name = team1Name;
  const team2Name = input.team2Name.trim();
  if (team2Name !== fixture.team2Name) body.team2Name = team2Name;

  const startsAt = fromIstWallClock(input.startsAt);
  if (minuteOf(startsAt) !== minuteOf(fixture.startsAt)) body.startsAt = startsAt;

  for (const field of ['seriesName', 'matchLabel', 'venue'] as const) {
    const next = (input[field] ?? '').trim();
    const current = fixture[field] ?? '';
    if (next === current) continue;
    body[field] = next === '' ? null : next;
  }

  if (!sameSet(input.audienceCountries, fixture.audienceCountries)) {
    body.audienceCountries = input.audienceCountries;
  }
  if (input.tracked !== fixture.tracked) body.tracked = input.tracked;
  if (input.status && input.status !== fixture.status) body.status = input.status;

  return body;
}
